import { CostMonth, type Me } from "@cloudserver/contracts";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  useMatches,
  useNavigate,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { Suspense, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CATALOGUE_TABS, CataloguePage } from "../pages/catalogue.js";
import { ConnectionPage } from "../pages/connection.js";
import { FIREWALL_TABS, FirewallPage } from "../pages/firewall.js";
import { FirewallCreatePage } from "../pages/firewall-new.js";
import { FirewallsPage } from "../pages/firewalls.js";
import { IMAGE_TABS, ImagesPage } from "../pages/images/page.js";
import { NETWORK_TABS, NetworkPage } from "../pages/network.js";
import { MonitoringPage } from "../pages/monitoring/page.js";
import { NotificationsPage } from "../pages/notifications.js";
import { NetworksPage } from "../pages/networks.js";
import { OverviewPage } from "../pages/overview.js";
import { ProjectAccessPage } from "../pages/project-access.js";
import { ProjectsPage } from "../pages/projects.js";
import { PublicIpsPage } from "../pages/public-ips.js";
import { SecurityPage } from "../pages/security.js";
import { SERVER_TABS } from "../pages/server/types.js";
import { ServersPage } from "../pages/servers.js";
import { CreateSearch } from "../pages/server-create/form.js";
import { SshKeysPage } from "../pages/ssh-keys.js";
import { VolumesPage } from "../pages/volumes.js";
import { FloatingIpsPage } from "../pages/floating-ips.js";
import { PlacementGroupsPage } from "../pages/placement-groups.js";
import { CertificatesPage } from "../pages/certificates.js";
import { LOAD_BALANCER_TABS, LoadBalancerPage } from "../pages/load-balancer.js";
import { LoadBalancerCreatePage } from "../pages/load-balancer-new.js";
import { LoadBalancersPage } from "../pages/load-balancers.js";
import { NotificationSettingsPage } from "../pages/profile/notifications.js";
import { ALERT_SCOPES, LEGACY_ORG_ALERT_TABS, PROJECT_ALERT_TABS } from "../pages/alerts/tabs.js";
import { API_TABS, MESSENGER_KINDS } from "../pages/api-access/tabs.js";
import { INSTANCE_TABS } from "../pages/apps/tabs.js";
import { Spinner } from "../ui/primitives.js";
import { useRememberProject } from "./last-project.js";
import { Shell } from "./shell.js";

/*
 * Pages loaded when first opened, so their code stays out of the start-up
 * bundle (docs/web.md, "Bundle"): the dashboard and the terminal bring big
 * libraries (React Flow, xterm), the API section, alerts, costs and users
 * are opened rarely, a server's pages and the create form are big. The
 * service worker precaches every chunk, so an installed app does not wait
 * for the network. A page whose chunk is gone after a deploy reloads once.
 */
const DashboardPage = lazyRouteComponent(
  () => import("../pages/dashboard/page.js"),
  "DashboardPage",
);
const TerminalWorkspacePage = lazyRouteComponent(
  () => import("../terminal/workspace.js"),
  "TerminalWorkspacePage",
);
const ProjectCostsPage = lazyRouteComponent(
  () => import("../pages/costs/project.js"),
  "ProjectCostsPage",
);
const OrgCostsPage = lazyRouteComponent(() => import("../pages/costs/org.js"), "OrgCostsPage");
const AlertsRedirect = lazyRouteComponent(
  () => import("../pages/alerts/redirect.js"),
  "AlertsRedirect",
);
const ProjectAlertsPage = lazyRouteComponent(
  () => import("../pages/alerts/project.js"),
  "ProjectAlertsPage",
);
const ApiAccessPage = lazyRouteComponent(
  () => import("../pages/api-access/page.js"),
  "ApiAccessPage",
);
const KeyWizardPage = lazyRouteComponent(
  () => import("../pages/api-access/key-wizard.js"),
  "KeyWizardPage",
);
const MessengersPage = lazyRouteComponent(
  () => import("../pages/api-access/messengers.js"),
  "MessengersPage",
);
const KeyPage = lazyRouteComponent(() => import("../pages/api-access/key.js"), "KeyPage");
const PolicyEditorPage = lazyRouteComponent(
  () => import("../pages/api-access/policy-editor.js"),
  "PolicyEditorPage",
);
const ServerPage = lazyRouteComponent(() => import("../pages/server/detail.js"), "ServerPage");
const ServerConsolePage = lazyRouteComponent(
  () => import("../pages/server/console/page.js"),
  "ServerConsolePage",
);
const ServerCreatePage = lazyRouteComponent(
  () => import("../pages/server-create/page.js"),
  "ServerCreatePage",
);
const UsersPage = lazyRouteComponent(() => import("../pages/users/list.js"), "UsersPage");
const UserPage = lazyRouteComponent(() => import("../pages/users/user.js"), "UserPage");
const ApiDocsPage = lazyRouteComponent(() => import("../pages/api-access/docs.js"), "ApiDocsPage");
const ConnectedAppPage = lazyRouteComponent(
  () => import("../pages/api-access/app.js"),
  "ConnectedAppPage",
);
const ConsentPage = lazyRouteComponent(() => import("../pages/oauth/consent.js"), "ConsentPage");
// «Приложения» (docs/packages.md): the wizard and the instance page bring the CEL evaluator.
const AppsPage = lazyRouteComponent(() => import("../pages/apps/page.js"), "AppsPage");
const AppInstallPage = lazyRouteComponent(() => import("../pages/apps/install.js"), "InstallPage");
const AppInstancePage = lazyRouteComponent(
  () => import("../pages/apps/instance.js"),
  "InstancePage",
);
const ApprovalPage = lazyRouteComponent(
  () => import("../pages/api-access/approval.js"),
  "ApprovalPage",
);

/** The spinner while a lazily loaded page arrives. */
function Loading({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return <Suspense fallback={<Spinner label={t("common.loading")} />}>{children}</Suspense>;
}

/** Routes exist only for a signed-in user; the auth screens sit outside the router. */
export interface RouterContext {
  me: Me;
}

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /** The page fills the whole area under the top bar: no sidebar, no margins. */
    fullBleed?: boolean;
    /** The page is a canvas: right of the sidebar, under the top bar, no margins. */
    canvas?: boolean;
  }
}

const root = createRootRouteWithContext<RouterContext>()({
  component: function Root() {
    const { me } = root.useRouteContext();
    const fullBleed = useMatches({ select: (m) => m.some((x) => x.staticData.fullBleed) });
    const canvas = useMatches({ select: (m) => m.some((x) => x.staticData.canvas) });
    useRememberProject();
    return (
      <Shell me={me} fullBleed={fullBleed} canvas={canvas}>
        <Outlet />
      </Shell>
    );
  },
});

/** Everyone without a second factor is sent to the security page first. */
const requireSecondFactor = ({ context }: { context: RouterContext }) => {
  if (context.me.secondFactorRequired) throw redirect({ to: "/security" });
};

/** The users section is for administrators; the API refuses everyone else anyway. */
const requireAdmin = ({ context }: { context: RouterContext }) => {
  requireSecondFactor({ context });
  if (context.me.role !== "admin") throw redirect({ to: "/" });
};

const projectsRoute = createRoute({
  getParentRoute: () => root,
  path: "/",
  beforeLoad: requireSecondFactor,
  // `connect`: opens «Подключить проект» (the switcher's «+ Новый проект»).
  validateSearch: z.object({ connect: z.boolean().optional().catch(undefined) }),
  component: function Projects() {
    const { connect } = projectsRoute.useSearch();
    const navigate = useNavigate({ from: projectsRoute.fullPath });
    return (
      <ProjectsPage
        connect={connect === true}
        onConnectClosed={() => void navigate({ search: {}, replace: true })}
      />
    );
  },
});

const overviewRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId",
  beforeLoad: requireSecondFactor,
  component: function Overview() {
    const { projectId } = overviewRoute.useParams();
    const { me } = overviewRoute.useRouteContext();
    return <OverviewPage projectId={projectId} me={me} />;
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/dashboard",
  beforeLoad: requireSecondFactor,
  staticData: { canvas: true },
  component: function Dashboard() {
    const { projectId } = dashboardRoute.useParams();
    return (
      <Loading>
        <DashboardPage projectId={projectId} />
      </Loading>
    );
  },
});

const monitoringRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/monitoring",
  beforeLoad: requireSecondFactor,
  component: function Monitoring() {
    const { projectId } = monitoringRoute.useParams();
    return <MonitoringPage projectId={projectId} />;
  },
});

const catalogueRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/catalogue",
  beforeLoad: requireSecondFactor,
  // `tab` is optional in links; an unknown value falls back to the first tab.
  validateSearch: z.object({ tab: z.enum(CATALOGUE_TABS).optional().catch(undefined) }),
  component: function Catalogue() {
    const { projectId } = catalogueRoute.useParams();
    const { tab } = catalogueRoute.useSearch();
    return <CataloguePage projectId={projectId} tab={tab ?? "server-types"} />;
  },
});

const connectionRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/connection",
  beforeLoad: requireSecondFactor,
  component: function Connection() {
    const { projectId } = connectionRoute.useParams();
    return <ConnectionPage projectId={projectId} />;
  },
});

const sshKeysRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/ssh-keys",
  beforeLoad: requireSecondFactor,
  component: function SshKeys() {
    const { projectId } = sshKeysRoute.useParams();
    return <SshKeysPage projectId={projectId} />;
  },
});

const serversRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/servers",
  beforeLoad: requireSecondFactor,
  component: function Servers() {
    const { projectId } = serversRoute.useParams();
    return <ServersPage projectId={projectId} />;
  },
});

const serverRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/servers/$serverId",
  beforeLoad: requireSecondFactor,
  validateSearch: z.object({
    tab: z.enum(SERVER_TABS).optional().catch(undefined),
    app: z
      .string()
      .regex(/^[a-z][a-z0-9_]{1,31}$/)
      .optional()
      .catch(undefined),
  }),
  component: function ServerDetail() {
    const { projectId, serverId } = serverRoute.useParams();
    const { tab, app } = serverRoute.useSearch();
    return (
      <Loading>
        <ServerPage projectId={projectId} serverId={serverId} tab={tab ?? "overview"} app={app} />
      </Loading>
    );
  },
});

const terminalsRoute = createRoute({
  getParentRoute: () => root,
  path: "/terminals",
  beforeLoad: requireSecondFactor,
  staticData: { fullBleed: true },
  component: function Terminals() {
    return (
      <Loading>
        <TerminalWorkspacePage />
      </Loading>
    );
  },
});

const serverConsoleRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/servers/$serverId/console",
  beforeLoad: requireSecondFactor,
  staticData: { fullBleed: true },
  component: function ServerConsole() {
    const { projectId, serverId } = serverConsoleRoute.useParams();
    return (
      <Loading>
        <ServerConsolePage projectId={projectId} serverId={serverId} />
      </Loading>
    );
  },
});

const networksRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/networks",
  beforeLoad: requireSecondFactor,
  component: function Networks() {
    const { projectId } = networksRoute.useParams();
    return <NetworksPage projectId={projectId} />;
  },
});

const networkRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/networks/$networkId",
  beforeLoad: requireSecondFactor,
  validateSearch: z.object({ tab: z.enum(NETWORK_TABS).optional().catch(undefined) }),
  component: function NetworkDetail() {
    const { projectId, networkId } = networkRoute.useParams();
    const { tab } = networkRoute.useSearch();
    return <NetworkPage projectId={projectId} networkId={networkId} tab={tab ?? "resources"} />;
  },
});

const firewallsRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/firewalls",
  beforeLoad: requireSecondFactor,
  component: function Firewalls() {
    const { projectId } = firewallsRoute.useParams();
    return <FirewallsPage projectId={projectId} />;
  },
});

const firewallCreateRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/firewalls/new",
  beforeLoad: requireSecondFactor,
  component: function FirewallCreate() {
    const { projectId } = firewallCreateRoute.useParams();
    return <FirewallCreatePage projectId={projectId} />;
  },
});

const firewallRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/firewalls/$firewallId",
  beforeLoad: requireSecondFactor,
  validateSearch: z.object({ tab: z.enum(FIREWALL_TABS).optional().catch(undefined) }),
  component: function FirewallDetail() {
    const { projectId, firewallId } = firewallRoute.useParams();
    const { tab } = firewallRoute.useSearch();
    return <FirewallPage projectId={projectId} firewallId={firewallId} tab={tab ?? "rules"} />;
  },
});

const appsRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/apps",
  beforeLoad: requireSecondFactor,
  component: function Apps() {
    const { projectId } = appsRoute.useParams();
    return (
      <Loading>
        <AppsPage projectId={projectId} tab="catalogue" />
      </Loading>
    );
  },
});

const appsInstalledRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/apps/installed",
  beforeLoad: requireSecondFactor,
  component: function AppsInstalled() {
    const { projectId } = appsInstalledRoute.useParams();
    return (
      <Loading>
        <AppsPage projectId={projectId} tab="installed" />
      </Loading>
    );
  },
});

const appInstallRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/apps/$packageId/install",
  beforeLoad: requireSecondFactor,
  component: function AppInstall() {
    const { projectId, packageId } = appInstallRoute.useParams();
    return (
      <Loading>
        <AppInstallPage projectId={projectId} packageId={packageId} />
      </Loading>
    );
  },
});

const appInstanceRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/apps/installed/$instanceId",
  beforeLoad: requireSecondFactor,
  validateSearch: z.object({ tab: z.enum(INSTANCE_TABS).optional().catch(undefined) }),
  component: function AppInstance() {
    const { projectId, instanceId } = appInstanceRoute.useParams();
    const { tab } = appInstanceRoute.useSearch();
    return (
      <Loading>
        <AppInstancePage projectId={projectId} instanceId={instanceId} tab={tab ?? "state"} />
      </Loading>
    );
  },
});

const publicIpsRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/ips",
  beforeLoad: requireSecondFactor,
  component: function PublicIps() {
    const { projectId } = publicIpsRoute.useParams();
    return <PublicIpsPage projectId={projectId} />;
  },
});

const volumesRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/volumes",
  beforeLoad: requireSecondFactor,
  component: function Volumes() {
    const { projectId } = volumesRoute.useParams();
    return <VolumesPage projectId={projectId} />;
  },
});

const floatingIpsRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/floating-ips",
  beforeLoad: requireSecondFactor,
  component: function FloatingIps() {
    const { projectId } = floatingIpsRoute.useParams();
    return <FloatingIpsPage projectId={projectId} />;
  },
});

const placementGroupsRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/placement-groups",
  beforeLoad: requireSecondFactor,
  component: function PlacementGroups() {
    const { projectId } = placementGroupsRoute.useParams();
    return <PlacementGroupsPage projectId={projectId} />;
  },
});

const loadBalancersRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/load-balancers",
  beforeLoad: requireSecondFactor,
  component: function LoadBalancers() {
    const { projectId } = loadBalancersRoute.useParams();
    return <LoadBalancersPage projectId={projectId} />;
  },
});

const loadBalancerCreateRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/load-balancers/new",
  beforeLoad: requireSecondFactor,
  component: function LoadBalancerCreate() {
    const { projectId } = loadBalancerCreateRoute.useParams();
    return <LoadBalancerCreatePage projectId={projectId} />;
  },
});

const loadBalancerRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/load-balancers/$loadBalancerId",
  beforeLoad: requireSecondFactor,
  validateSearch: z.object({ tab: z.enum(LOAD_BALANCER_TABS).optional().catch(undefined) }),
  component: function LoadBalancerDetail() {
    const { projectId, loadBalancerId } = loadBalancerRoute.useParams();
    const { tab } = loadBalancerRoute.useSearch();
    return (
      <LoadBalancerPage
        projectId={projectId}
        loadBalancerId={loadBalancerId}
        tab={tab ?? "overview"}
      />
    );
  },
});

const certificatesRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/certificates",
  beforeLoad: requireSecondFactor,
  component: function Certificates() {
    const { projectId } = certificatesRoute.useParams();
    return <CertificatesPage projectId={projectId} />;
  },
});

const imagesRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/images",
  beforeLoad: requireSecondFactor,
  validateSearch: z.object({ tab: z.enum(IMAGE_TABS).optional().catch(undefined) }),
  component: function Images() {
    const { projectId } = imagesRoute.useParams();
    const { tab } = imagesRoute.useSearch();
    return <ImagesPage projectId={projectId} tab={tab ?? "snapshots"} />;
  },
});

const serverCreateRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/servers/new",
  beforeLoad: requireSecondFactor,
  // The whole form lives in the search params, so a configuration can be shared.
  validateSearch: CreateSearch,
  component: function ServerCreate() {
    const { projectId } = serverCreateRoute.useParams();
    const search = serverCreateRoute.useSearch();
    const navigate = useNavigate({ from: serverCreateRoute.fullPath });
    return (
      <Loading>
        <ServerCreatePage
          projectId={projectId}
          search={search}
          onChange={(change) =>
            void navigate({ search: (prev) => ({ ...prev, ...change }), replace: true })
          }
        />
      </Loading>
    );
  },
});

const projectAccessRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/access",
  beforeLoad: requireSecondFactor,
  component: function ProjectAccess() {
    const { projectId } = projectAccessRoute.useParams();
    return <ProjectAccessPage projectId={projectId} />;
  },
});

/** `?month=YYYY-MM`; anything else falls back to the current month. */
const CostSearch = z.object({ month: CostMonth.optional().catch(undefined) });

const projectCostsRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/costs",
  beforeLoad: requireSecondFactor,
  // `raise`: opens the raise request (the link of a change refused for the budget).
  validateSearch: CostSearch.extend({ raise: z.boolean().optional().catch(undefined) }),
  component: function ProjectCosts() {
    const { projectId } = projectCostsRoute.useParams();
    const { month, raise } = projectCostsRoute.useSearch();
    const navigate = useNavigate({ from: projectCostsRoute.fullPath });
    return (
      <Loading>
        <ProjectCostsPage
          projectId={projectId}
          month={month}
          raise={raise === true}
          onMonth={(next) => void navigate({ search: { month: next }, replace: true })}
        />
      </Loading>
    );
  },
});

const orgCostsRoute = createRoute({
  getParentRoute: () => root,
  path: "/costs",
  beforeLoad: requireAdmin,
  validateSearch: CostSearch,
  component: function OrgCosts() {
    const { month } = orgCostsRoute.useSearch();
    const navigate = useNavigate({ from: orgCostsRoute.fullPath });
    return (
      <Loading>
        <OrgCostsPage
          month={month}
          onMonth={(next) => void navigate({ search: { month: next }, replace: true })}
        />
      </Loading>
    );
  },
});

const notificationsRoute = createRoute({
  getParentRoute: () => root,
  path: "/notifications",
  beforeLoad: requireSecondFactor,
  component: NotificationsPage,
});

const usersRoute = createRoute({
  getParentRoute: () => root,
  path: "/users",
  beforeLoad: requireAdmin,
  component: function Users() {
    return (
      <Loading>
        <UsersPage />
      </Loading>
    );
  },
});

const userRoute = createRoute({
  getParentRoute: () => root,
  path: "/users/$userId",
  beforeLoad: requireAdmin,
  component: function User() {
    const { userId } = userRoute.useParams();
    // A new page per person, so its form starts from that person's data.
    return (
      <Loading>
        <UserPage key={userId} userId={userId} />
      </Loading>
    );
  },
});

/** The section's old address: `?tab=` leads to that view's own address (docs/api-keys.md). */
const apiAccessRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access",
  validateSearch: z.object({ tab: z.enum(API_TABS).optional().catch(undefined) }),
  beforeLoad: ({ context, search }) => {
    requireSecondFactor({ context });
    throw redirect({ to: `/api-access/${search.tab ?? "keys"}` as "/api-access/keys" });
  },
});

const apiKeysRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/keys",
  beforeLoad: requireSecondFactor,
  component: () => (
    <Loading>
      <ApiAccessPage view="keys" />
    </Loading>
  ),
});

const apiServiceAccountsRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/service-accounts",
  beforeLoad: requireSecondFactor,
  component: () => (
    <Loading>
      <ApiAccessPage view="service-accounts" />
    </Loading>
  ),
});

const apiPoliciesRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/policies",
  beforeLoad: requireSecondFactor,
  component: () => (
    <Loading>
      <ApiAccessPage view="policies" />
    </Loading>
  ),
});

const apiEndpointsRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/endpoints",
  beforeLoad: requireSecondFactor,
  component: () => (
    <Loading>
      <ApiAccessPage view="endpoints" />
    </Loading>
  ),
});

const apiApprovalsRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/approvals",
  beforeLoad: requireSecondFactor,
  component: () => (
    <Loading>
      <ApiAccessPage view="approvals" />
    </Loading>
  ),
});

const apiAppsRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/apps",
  beforeLoad: requireSecondFactor,
  component: () => (
    <Loading>
      <ApiAccessPage view="apps" />
    </Loading>
  ),
});

const apiActivityRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/activity",
  beforeLoad: requireSecondFactor,
  component: () => (
    <Loading>
      <ApiAccessPage view="activity" />
    </Loading>
  ),
});

/** Messenger connectors (docs/api-keys.md, docs/alerts.md): Telegram bots first. */
const apiMessengersRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/messengers",
  beforeLoad: requireSecondFactor,
  validateSearch: z.object({ kind: z.enum(MESSENGER_KINDS).optional().catch(undefined) }),
  component: function ApiMessengers() {
    const { kind } = apiMessengersRoute.useSearch();
    return (
      <Loading>
        <MessengersPage kind={kind ?? "telegram"} />
      </Loading>
    );
  },
});

const apiKeyNewRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/keys/new",
  beforeLoad: requireSecondFactor,
  component: function ApiKeyNew() {
    return (
      <Loading>
        <KeyWizardPage />
      </Loading>
    );
  },
});

const apiKeyRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/keys/$keyId",
  beforeLoad: requireSecondFactor,
  component: function ApiKey() {
    const { keyId } = apiKeyRoute.useParams();
    return (
      <Loading>
        <KeyPage key={keyId} keyId={keyId} />
      </Loading>
    );
  },
});

const apiPolicyNewRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/policies/new",
  beforeLoad: requireSecondFactor,
  component: function ApiPolicyNew() {
    return (
      <Loading>
        <PolicyEditorPage policyId={null} />
      </Loading>
    );
  },
});

const apiPolicyRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/policies/$policyId",
  beforeLoad: requireSecondFactor,
  component: function ApiPolicy() {
    const { policyId } = apiPolicyRoute.useParams();
    return (
      <Loading>
        <PolicyEditorPage key={policyId} policyId={policyId} />
      </Loading>
    );
  },
});

const apiDocsRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/docs",
  beforeLoad: requireSecondFactor,
  component: function ApiDocs() {
    return (
      <Loading>
        <ApiDocsPage />
      </Loading>
    );
  },
});

const connectedAppRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/apps/$appId",
  beforeLoad: requireSecondFactor,
  component: function ConnectedApp() {
    const { appId } = connectedAppRoute.useParams();
    return <ConnectedAppPage key={appId} appId={appId} />;
  },
});

/** One request waiting for (or past) approval (docs/approvals.md); notifications link here. */
const apiApprovalRoute = createRoute({
  getParentRoute: () => root,
  path: "/api-access/approvals/$approvalId",
  beforeLoad: requireSecondFactor,
  component: function ApiApproval() {
    const { approvalId } = apiApprovalRoute.useParams();
    return <ApprovalPage key={approvalId} approvalId={approvalId} />;
  },
});

/** The OAuth consent screen (docs/oauth.md); the authorization endpoint sends the browser here. */
const oauthConsentRoute = createRoute({
  getParentRoute: () => root,
  path: "/oauth/consent",
  beforeLoad: requireSecondFactor,
  validateSearch: z.object({
    request: z.string().max(64).optional().catch(undefined),
    error: z.string().max(64).optional().catch(undefined),
  }),
  component: function OAuthConsent() {
    const { request, error } = oauthConsentRoute.useSearch();
    return <ConsentPage requestId={request ?? null} error={error ?? null} />;
  },
});

/** The organisation's alerts page until 2026-10-22: old links go where its parts are now. */
const alertsRoute = createRoute({
  getParentRoute: () => root,
  path: "/alerts",
  beforeLoad: requireSecondFactor,
  validateSearch: z.object({ tab: z.enum(LEGACY_ORG_ALERT_TABS).optional().catch(undefined) }),
  component: function Alerts() {
    const { tab } = alertsRoute.useSearch();
    return (
      <Loading>
        <AlertsRedirect tab={tab} />
      </Loading>
    );
  },
});

const projectAlertsRoute = createRoute({
  getParentRoute: () => root,
  path: "/projects/$projectId/alerts",
  beforeLoad: requireSecondFactor,
  validateSearch: z.object({
    tab: z.enum(PROJECT_ALERT_TABS).optional().catch(undefined),
    scope: z.enum(ALERT_SCOPES).optional().catch(undefined),
  }),
  component: function ProjectAlerts() {
    const { projectId } = projectAlertsRoute.useParams();
    const { tab, scope } = projectAlertsRoute.useSearch();
    return (
      <Loading>
        <ProjectAlertsPage projectId={projectId} tab={tab ?? "active"} scope={scope ?? "project"} />
      </Loading>
    );
  },
});

const notificationSettingsRoute = createRoute({
  getParentRoute: () => root,
  path: "/profile/notifications",
  beforeLoad: requireSecondFactor,
  component: NotificationSettingsPage,
});

const securityRoute = createRoute({
  getParentRoute: () => root,
  path: "/security",
  component: function Security() {
    const { me } = securityRoute.useRouteContext();
    return <SecurityPage me={me} />;
  },
});

const routeTree = root.addChildren([
  projectsRoute,
  overviewRoute,
  dashboardRoute,
  monitoringRoute,
  catalogueRoute,
  connectionRoute,
  sshKeysRoute,
  serversRoute,
  serverRoute,
  serverConsoleRoute,
  terminalsRoute,
  networksRoute,
  networkRoute,
  firewallsRoute,
  firewallCreateRoute,
  firewallRoute,
  serverCreateRoute,
  publicIpsRoute,
  imagesRoute,
  projectAccessRoute,
  usersRoute,
  userRoute,
  securityRoute,
  volumesRoute,
  floatingIpsRoute,
  placementGroupsRoute,
  projectCostsRoute,
  orgCostsRoute,
  notificationsRoute,
  loadBalancersRoute,
  loadBalancerCreateRoute,
  loadBalancerRoute,
  certificatesRoute,
  apiAccessRoute,
  apiKeysRoute,
  apiServiceAccountsRoute,
  apiPoliciesRoute,
  apiEndpointsRoute,
  apiApprovalsRoute,
  apiAppsRoute,
  apiActivityRoute,
  apiMessengersRoute,
  apiKeyNewRoute,
  apiKeyRoute,
  apiPolicyNewRoute,
  apiPolicyRoute,
  apiDocsRoute,
  connectedAppRoute,
  apiApprovalRoute,
  oauthConsentRoute,
  alertsRoute,
  projectAlertsRoute,
  notificationSettingsRoute,
  appsRoute,
  appsInstalledRoute,
  appInstallRoute,
  appInstanceRoute,
]);

export function makeRouter(me: Me) {
  return createRouter({ routeTree, context: { me }, defaultPreload: "intent" });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
